import { type AbstractControl, type UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';

import { entrySet } from '@fcom/core/utils';

import {
  AdditionalExpenses,
  AdditionalExpensesInputs,
  EUClaimOptions,
  FormCategories,
  MultiStepFormSteps,
} from '../../../interfaces';
import { PRICE_PATTERN } from '../../../utils';

export const RATING_SCHEME_MAP = { veryPoor: 1, poor: 2, okay: 3, good: 4, excellent: 5 };

export const resolveEuStandardCompensationGiftCardValue = (key: EUClaimOptions): number => {
  switch (key) {
    case EUClaimOptions.SHORT:
      return 375;
    case EUClaimOptions.SHORT_HALF:
      return 188;
    case EUClaimOptions.MIDDLE:
      return 600;
    case EUClaimOptions.MIDDLE_HALF:
      return 300;
    case EUClaimOptions.LONG:
      return 900;
    case EUClaimOptions.LONG_HALF:
      return 450;
    default:
      return 0;
  }
};

export const resolveEuStandardCompensationCashValue = (key: EUClaimOptions): number => {
  switch (key) {
    case EUClaimOptions.SHORT:
      return 250;
    case EUClaimOptions.SHORT_HALF:
      return 125;
    case EUClaimOptions.MIDDLE:
      return 400;
    case EUClaimOptions.MIDDLE_HALF:
      return 200;
    case EUClaimOptions.LONG:
      return 600;
    case EUClaimOptions.LONG_HALF:
      return 300;
    default:
      return 0;
  }
};

export const resolveFirstStepForFlow = (step: FormCategories | undefined): MultiStepFormSteps | undefined => {
  if (step === FormCategories.FLIGHT_DISRUPTION) {
    return MultiStepFormSteps.BOOKING_INFORMATION;
  } else if (step === FormCategories.BAGGAGE_IRREGULARITIES || step === FormCategories.BOOKING_AND_TRAVEL_EXPERIENCE) {
    return MultiStepFormSteps.SECOND_CATEGORY;
  }
  return undefined;
};

export const getBaggageExpenseControls = (
  oldGroup: AbstractControl<UntypedFormGroup>
): Record<AdditionalExpensesInputs[number], [string, ValidatorFn[]]> => {
  return {
    [AdditionalExpensesInputs.CLOTH_AND_COSMETIC_COST]: [
      oldGroup?.get(AdditionalExpensesInputs.CLOTH_AND_COSMETIC_COST)?.value || '',
      [Validators.pattern(PRICE_PATTERN)],
    ],
    [AdditionalExpensesInputs.RENTED_COST]: [
      oldGroup?.get(AdditionalExpensesInputs.RENTED_COST)?.value || '',
      [Validators.pattern(PRICE_PATTERN)],
    ],
  };
};

export const getAdditionalExpenseControls = (
  oldGroup: AbstractControl<UntypedFormGroup>
): Record<AdditionalExpensesInputs[number], [string, ValidatorFn]> => {
  return {
    [AdditionalExpensesInputs.TRANSPORT_COST]: [
      oldGroup?.get(AdditionalExpensesInputs.TRANSPORT_COST)?.value || '',
      Validators.pattern(PRICE_PATTERN),
    ],
    [AdditionalExpensesInputs.HOTEL_COST]: [
      oldGroup?.get(AdditionalExpensesInputs.HOTEL_COST)?.value || '',
      Validators.pattern(PRICE_PATTERN),
    ],
    [AdditionalExpensesInputs.PHONE_COST]: [
      oldGroup?.get(AdditionalExpensesInputs.PHONE_COST)?.value || '',
      Validators.pattern(PRICE_PATTERN),
    ],
    [AdditionalExpensesInputs.MEAL_COST]: [
      oldGroup?.get(AdditionalExpensesInputs.MEAL_COST)?.value || '',
      Validators.pattern(PRICE_PATTERN),
    ],
  };
};

export const validateAtLeastOneFieldFilled = (): ValidatorFn => {
  return (group: UntypedFormGroup): { pattern: true } | null => {
    if (group.value[AdditionalExpensesInputs.ADDITIONAL_EXPENSES] === AdditionalExpenses.NO) {
      return null;
    }
    const atLeastOneFieldFilled: boolean = entrySet(group.value)
      .filter((field) => field.key !== AdditionalExpensesInputs.ADDITIONAL_EXPENSES)
      .some((field) => field.value !== null && field.value !== '');
    if (atLeastOneFieldFilled) {
      return null;
    }
    return { pattern: true };
  };
};
